/**
 * A typed form field against the number it was seeded from.
 *
 * Every nutrition form in both clients compared these by hand, and the
 * comparison was wrong the moment a field could be null:
 *
 *     parseDecimal(values[key] ?? "") !== Number(food[key] ?? 0)
 *
 * A null seeds a blank box, `parseDecimal("")` is `NaN` by design, and
 * `NaN !== 0` is true — so a food whose fibre was simply never recorded
 * reported fibre as *edited* the instant the panel opened. That raised the
 * "who gets this fix" question about a food nobody had touched, and sent
 * `NaN` as the new value, which serialises to JSON `null` and clears the
 * figure on a row every other scanner of that barcode shares.
 *
 * The three cases have to be told apart, because they mean different things
 * on the wire:
 *
 * - blank where there was nothing  → unchanged, send nothing
 * - blank where there was a value  → *cleared*, send `null` deliberately
 * - a different number             → send the number
 *
 * Comparison is at the precision the field was seeded at. Without that,
 * seeding "12.3" from a stored 12.34 and saving without touching anything
 * files a correction for a rounding difference — the trap `reading.ts` already
 * documents, and the reason `decimals` is part of the spec rather than a
 * constant.
 */

import { parseDecimal } from "./parse";

export interface FigureSpec {
  /**
   * Decimal places the field is shown, seeded and compared at.
   *
   * One number, one place, in one object — a field seeded at a different
   * precision from the one it is compared at reports itself as edited forever.
   */
  decimals?: number;
  /**
   * Stored value to displayed value, where they differ.
   *
   * Only salt does anything here: it is stored as sodium in milligrams and
   * typed in grams, because grams is what the packet says. Everything else
   * leaves this alone.
   */
  display?: (stored: number) => number;
  /** Displayed value back to stored. Must be the exact inverse of `display`. */
  store?: (shown: number) => number;
  /**
   * Whether blanking the field is allowed.
   *
   * Calories are required — a food without them cannot be logged — so a blank
   * there is a mistake to report, not an instruction to clear.
   */
  required?: boolean;
}

export type FigureEdit =
  /** Nothing to send. */
  | { state: "unchanged" }
  /** Deliberately emptied. Send `null` — the server takes the figure away. */
  | { state: "cleared" }
  /** A number to send. */
  | { state: "set"; value: number }
  /** Something that is not a number, or is negative. */
  | { state: "invalid" };

const identity = (value: number) => value;

function at(value: number, decimals: number): number {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}

/**
 * The string to put in the box for a stored value.
 *
 * Null seeds a blank, which is the honest rendering: the field is asking a
 * question, and a zero in it would be answering one.
 */
export function seedFigure(stored: number | null | undefined, spec: FigureSpec = {}): string {
  if (typeof stored !== "number" || !Number.isFinite(stored)) return "";
  const display = spec.display ?? identity;
  return String(at(display(stored), spec.decimals ?? 1));
}

/** What a typed field says about the value it was seeded from. */
export function figureChange(
  typed: string | undefined,
  stored: number | null | undefined,
  spec: FigureSpec = {},
): FigureEdit {
  const decimals = spec.decimals ?? 1;
  const display = spec.display ?? identity;
  const store = spec.store ?? identity;
  const had = typeof stored === "number" && Number.isFinite(stored);

  if ((typed ?? "").trim() === "") {
    if (spec.required) return had ? { state: "invalid" } : { state: "unchanged" };
    return had ? { state: "cleared" } : { state: "unchanged" };
  }

  const shown = parseDecimal(typed);
  if (!Number.isFinite(shown) || shown < 0) return { state: "invalid" };

  // Compared in the unit that was typed, at the precision it was shown. Salt
  // is the field this matters for: a typed 1.2 g against a stored 483 mg is a
  // difference of rounding, not of intent, and comparing in milligrams would
  // file a phantom correction on every save.
  if (had && at(shown, decimals) === at(display(stored as number), decimals)) {
    return { state: "unchanged" };
  }
  return { state: "set", value: store(shown) };
}

/** Whether an edit has anything to send. */
export function figureMoved(edit: FigureEdit): boolean {
  return edit.state === "cleared" || edit.state === "set";
}

/** The value to put on the wire, for an edit that moved. */
export function figureValue(edit: FigureEdit): number | null {
  return edit.state === "set" ? edit.value : null;
}
